import { Text } from '@ltvco/refresh-lib/theme';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  COURT_CASES_V2_ROUTES,
  LEGAL_INTELLIGENCE_ROUTES,
  parseLegalIntelSection,
  withLegalIntelSection,
} from '../../../constants';
import {
  InlineLoading,
  ErrorStateContainer,
  LegalSearchBreadcrumbs,
} from '../../shared';
import { useJudgeDetails } from '../api';

import { JudgeDetailsContainer } from './components';
import { JudgeCaseLawAuthored } from './JudgeCaseLawAuthored';
import { JudgeCasesAssigned } from './JudgeCasesAssigned';
import {
  JudgeBiographicalInfo,
  JudgeCareerHistory,
  JudgeEducation,
} from './judgeDetailSections';

import type { JudgePosition } from '../../types';

export const JudgeDetails = ({ judgeId }: { judgeId?: string | null }) => {
  const location = useLocation();
  const { id: paramId } = useParams<{ id: string }>();
  const [positions, setPositions] = useState<JudgePosition[]>([]);
  const judgeIdToUse = judgeId ?? paramId ?? '';

  const result = useJudgeDetails(judgeIdToUse);
  const { judgeQuery, isLoading } = result;

  const {
    data: judgeData,
    isError: isJudgeError,
    error: judgeError,
  } = judgeQuery;

  useEffect(() => {
    const idToUse = judgeId ?? paramId;
    const stored = localStorage.getItem(`positions_${idToUse}`);
    if (stored) {
      try {
        setPositions(JSON.parse(stored));
      } catch {
        // Ignore malformed data
      }
      localStorage.removeItem(`positions_${idToUse}`);
    }
  }, [judgeId, paramId]);

  if (isLoading) {
    return <InlineLoading />;
  }

  if (isJudgeError || !judgeData) {
    const errorMessage =
      judgeError instanceof Error ? judgeError.message : 'Judge not found';
    return (
      <ErrorStateContainer>
        <Text color="error">Error: {errorMessage}</Text>
      </ErrorStateContainer>
    );
  }

  const uniqueCourts = Array.from(
    new Set(positions?.map((p) => p.court).filter((court) => court) || [])
  );
  const judicialPositions =
    positions?.filter(
      (position: JudgePosition) => position.position_type !== null
    ) || [];
  const nonJudicialPositions =
    positions?.filter(
      (position: JudgePosition) => position.position_type === null
    ) || [];

  const fullName = `${judgeData.name_first} ${judgeData.name_last}`;
  const isEmbedded = Boolean(judgeId);
  const section = parseLegalIntelSection(location.search, location.pathname);

  return (
    <JudgeDetailsContainer>
      {!isEmbedded && (
        <LegalSearchBreadcrumbs
          segments={[
            {
              label: 'Legal Intelligence',
              to: LEGAL_INTELLIGENCE_ROUTES.BASE,
            },
            {
              label: 'Judge Search',
              to: withLegalIntelSection(
                COURT_CASES_V2_ROUTES.JUDGE_SEARCH,
                section
              ),
            },
            { label: fullName },
          ]}
        />
      )}
      <JudgeBiographicalInfo
        judgeData={judgeData}
        courts={uniqueCourts}
        aba_rating={judgeData.aba_rating}
      />
      <JudgeEducation judgeData={judgeData} />
      <JudgeCareerHistory
        judicialPositions={judicialPositions}
        nonJudicialPositions={nonJudicialPositions}
      />
      <JudgeCaseLawAuthored judgeId={judgeData.id} judgeName={fullName} />
      <JudgeCasesAssigned judgeId={judgeData.id} judgeName={fullName} />
    </JudgeDetailsContainer>
  );
};
